import React, { useState, useContext, useMemo, useEffect } from 'react';
import {
  GridLegacy as Grid,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
} from '@mui/material';

import StagingDataTable from '../StagingDataTable';
import ColumnDefinition from '../../constants/ColumnDefinition';
import StagingFilterBuilder from '../StagingFilterBuilder';
import FilterContext from '../../contexts/FilterContext';

export const EquipmentSelectorDialog = ({
  open,
  title,
  onSubmit,
  buildingId,
  loading,
  onClose,
  editing,
  filterDefBuilders,
  getConfidence,
  promotedEquipment,
  equipTypes,
  defaultSelection,
}) => {
  const { filters, resetFilters } = useContext(FilterContext);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  const filterDefs = useMemo(
    () => filterDefBuilders.map((builder) => builder({ getConfidence, selectedTargets })),
    [filterDefBuilders, getConfidence, selectedTargets]
  );

  const otherEquipment = useMemo(
    () => promotedEquipment.filter((e) => e.name !== editing.name),
    [promotedEquipment, editing]
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedTargets(defaultSelection(editing));
    // Also tracking open to ensure proper population if equipment is closed and reopened
  }, [defaultSelection, editing, open]);

  const handleClose = (resetState) => {
    resetFilters();
    if (resetState) setSelectedTargets(defaultSelection(editing));
    onClose();
  };

  const handleSubmit = () => {
    setButtonsDisabled(true);
    onSubmit(selectedTargets).then((success) => {
      setButtonsDisabled(false);
      if (success) {
        handleClose(true);
      }
    });
  };

  const filteredTargets = useMemo(
    () => otherEquipment.filter((e) => filters.every((f) => f.filter(e))),
    [otherEquipment, filters]
  );

  const columns = [];
  if (getConfidence !== undefined) {
    columns.push(
      new ColumnDefinition({
        id: 'confidence',
        label: 'Confidence',
        type: 'linear',
        hideable: true,
        render: getConfidence,
      })
    );
  }
  columns.push(
    new ColumnDefinition({
      id: 'equip_name',
      label: 'Equipment Name',
      type: 'text',
      hideable: true,
      render: (e) => e.name,
    })
  );
  columns.push(
    new ColumnDefinition({
      id: 'equip_type',
      label: 'Equipment Type',
      type: 'text',
      hideable: true,
      render: (e) => equipTypes[e.equip_type_id]?.nameAbbr,
    })
  );

  if (!open) return null;

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid item xs={12}>
          <StagingFilterBuilder filterDefs={filterDefs} />
        </Grid>
        <Grid item xs={12}>
          <StagingDataTable
            columns={columns}
            allData={otherEquipment}
            filteredData={filteredTargets}
            rowIdKey="name"
            loading={loading}
            buildingId={buildingId}
            selectedIds={selectedTargets}
            onItemSelected={(newEquipNames) => setSelectedTargets(newEquipNames)}
          />
        </Grid>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          color="success"
          disabled={buttonsDisabled}
        >
          Save
        </Button>
        <Button
          variant="contained"
          onClick={() => handleClose(true)}
          color="error"
          disabled={buttonsDisabled}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
